'use client';

import dynamic from 'next/dynamic';
import React, { useEffect, useMemo, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslation, Text, Container, Icon, useResizeObserver, Link, Image } from '@weareconceptstudio/core';
import ApplicationButton from './ApplicationButton';

gsap.registerPlugin(ScrollTrigger);

//* HOC's
import { withDataContext } from '@/context';

//* Helpers
import { config } from '@/helpers';

//* Components
const RatingAndReview = dynamic(() => import('../../modules/_Sections/RatingAndReview'), { ssr: false });

//* Style
import FooterStyle from './style';

const Footer = ({ globalData }) => {
	const { translate, selectedLang } = useTranslation();

	//! Navigation
	const router = useRouter();
	let pathname = usePathname();

	pathname = pathname.replace(selectedLang + '/', '');
	pathname = pathname.split('/')[1];

	//! Checks
	const useBurgerMenuHide = useMemo(() => {
		return [
			'sign-in',
			'sign-up',
			'forgot-password',
			'reset-password',
			'verify-phone',
			'thank-you-message',
			'payment-failed',
			'account',
			'cart',
			'checkout',
			'menu',
		].includes(pathname);
	}, [pathname]);

	//! Refs
	const footerRef = useRef();
	const scrollTrigger = useRef();

	//! Footer Column 1
	const column1 = useMemo(() => {
		return Object.values(config.routes).map((route, index) => {
			return (
				route?.footer_column1 && (
					<Link
						key={index}
						url={route.path}
						className={'width-fit-content color-hover'}>
						<Text
							className={'p p3'}
							text={route.name}
						/>
					</Link>
				)
			);
		});
	}, [config.routes]);

	//! Footer Column 2
	const column2 = useMemo(() => {
		return Object.values(config.routes).map((route, index) => {
			return (
				route?.footer_column2 && (
					<Link
						key={index}
						url={route.path}
						className={'width-fit-content color-hover'}>
						<Text
							className={'p p3'}
							text={route.name}
						/>
					</Link>
				)
			);
		});
	}, [config.routes]);

	//! Resizes
	const resizes = useResizeObserver(typeof document !== 'undefined' ? document.querySelector('body') : null);

	useEffect(() => {
		const burgerMenu = document.getElementById('burger-menu-visible');
		const subscribeWrapper = document.getElementById('subscribe-wrapper');
		const exclusiveBenefitsWrapper = document.getElementById('exclusive-benefits-wrapper');

		if (footerRef.current) {
			if (!useBurgerMenuHide) {
				burgerMenu.style.transition = 'transform 0.1s ease-out, opacity 0.1s ease-out';
			}

			scrollTrigger.current = ScrollTrigger.create({
				start: 'top bottom',
				end: 'top top',
				trigger: footerRef.current,
				// markers: true,
				onEnter: () => {
					if (burgerMenu && !useBurgerMenuHide) {
						burgerMenu.style.transform = 'scale(0)';
						burgerMenu.style.opacity = '0';
					}

					if (subscribeWrapper) {
						subscribeWrapper.style.opacity = '0';
						subscribeWrapper.style.pointerEvents = 'none';

						subscribeWrapper.style.transition = 'transform 0.1s ease-out, opacity 0.1s ease-out';
					}

					if (exclusiveBenefitsWrapper) {
						exclusiveBenefitsWrapper.style.opacity = '0';
						exclusiveBenefitsWrapper.style.pointerEvents = 'none';

						exclusiveBenefitsWrapper.style.transition = 'transform 0.1s ease-out, opacity 0.1s ease-out';
					}
				},
				onLeaveBack: () => {
					if (burgerMenu && !useBurgerMenuHide) {
						burgerMenu.style.transform = 'scale(1)';
						burgerMenu.style.opacity = '1';
					}

					if (subscribeWrapper) {
						subscribeWrapper.style.opacity = '1';
						subscribeWrapper.style.pointerEvents = 'all';

						subscribeWrapper.style.transition = 'transform 0.1s ease-out, opacity 0.1s ease-out';
					}

					if (exclusiveBenefitsWrapper) {
						exclusiveBenefitsWrapper.style.opacity = '1';
						exclusiveBenefitsWrapper.style.pointerEvents = 'all';

						exclusiveBenefitsWrapper.style.transition = 'transform 0.1s ease-out, opacity 0.1s ease-out';
					}
				},
			});
		}

		return () => {
			if (scrollTrigger.current) {
				scrollTrigger.current.kill();
			}

			if (burgerMenu && !useBurgerMenuHide) {
				burgerMenu.style.transform = 'scale(1)';
				burgerMenu.style.opacity = '1';
			}

			if (subscribeWrapper) {
				subscribeWrapper.style.opacity = '1';
				subscribeWrapper.style.pointerEvents = 'all';
			}

			if (exclusiveBenefitsWrapper) {
				exclusiveBenefitsWrapper.style.opacity = '1';
				exclusiveBenefitsWrapper.style.pointerEvents = 'all';
			}
		};
	}, [resizes, footerRef, useBurgerMenuHide]);

	return (
		<FooterStyle ref={footerRef}>
			<div className='footer-first-container'>
				<Container
					row
					full>
					<div className='col-6 col-m-12'>
						<div className='left-block'>
							<Image
								alt={'Murakami City'}
								src={'/images/svg/whiteLogo.svg'}
								className={'logo-block cursor-pointer'}
								onClick={() => router.push(config.routes.home.path)}
							/>

							<ApplicationButton />

							<RatingAndReview />

							<div className='socials-block'>
								<Link
									external
									url={globalData.socials?.instagram}>
									<Icon name={'instagram'} />
								</Link>

								<Link
									external
									url={globalData.socials?.facebook}>
									<Icon name={'facebook'} />
								</Link>
							</div>
						</div>
					</div>

					<div className='col-6 col-m-12'>
						<div className='right-block'>
							<div className='block title'>
								<Text
									className={'p2 font-montserrat-arm-medium'}
									text={'shop'}
								/>

								<div className='flex-block large'>{column1}</div>
							</div>

							<div className='block title'>
								<Text
									className={'p2 font-montserrat-arm-medium'}
									text={'help'}
								/>

								<div className='flex-block large'>{column2}</div>
							</div>

							<div className='block large'>
								{globalData.delivery?.text ? (
									<div className='flex-block small'>
										<Text className={'p3 font-montserrat-arm-medium'}>
											{globalData.delivery.title}
										</Text>

										<Text className={'p3 font-montserrat-arm-medium'}>
											{globalData.delivery.text}
										</Text>
									</div>
								) : null}

								<div className='flex-block small'>
									{globalData?.phones?.length > 0
										? globalData.phones.map((phone, index) => (
												<Link
													external
													key={index}
													className={'width-fit-content color-hover'}
													url={`tel:${phone}`}>
													<Text className={'p3 font-montserrat-arm-medium underline'}>
														{phone}
													</Text>
												</Link>
											))
										: null}
								</div>

								<div className='flex-block'>
									<Link
										className={'color-hover '}
										url={globalData.address?.link}
										external>
										<Text className={'p3 font-montserrat-arm-medium underline'}>
											{globalData.address?.line_1}
										</Text>
										<Text className={'p3 font-montserrat-arm-medium underline'}>
											{globalData.address?.line_2}
										</Text>
									</Link>
								</div>
							</div>
						</div>
					</div>
				</Container>
			</div>

			<div className='footer-last-container'>
				<Container
					row
					full>
					<div className='col-6 col-m-12'>
						<div className='inner-block'>
							<Text className={'p3'}>
								{translate('copyWriter', {
									date: new Date().getFullYear(),
								})}
							</Text>
						</div>
					</div>

					<div className='col-6 col-m-12 mt-16'>
						<div className='inner-block'>
							<Text className={'p3'}>
								{translate('createdBy', {
									officeName: (
										<Link
											external
											className={'p3 font-montserrat-arm-bold color-hover'}
											url={'https://conceptstudio.com/'}>
											Concept Studio
										</Link>
									),
								})}
							</Text>
						</div>
					</div>
				</Container>
			</div>
		</FooterStyle>
	);
};

export default withDataContext(Footer, ['globalData']);
